import { useEffect } from "react";
import { motion } from "framer-motion";
import Founder from "../images/Founder.png";

export default function AboutPage() {

  // PAGE TITLE + META (NO HELMET)
  useEffect(() => {
    document.title = "About Us | Ledot Prime - GPS Tracking & Security";
  }, []);

  return (
    <>
      {/* BREADCRUMB SCHEMA */}
      <script type="application/ld+json">
        {`
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://ledotprime.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "About",
              "item": "https://ledotprime.com/about"
            }
          ]
        }
        `}
      </script>

      <div className="w-full flex flex-col items-center bg-white text-gray-800 pt-20">
      
        {/* HERO SECTION */}
        <section className="w-full max-w-6xl py-24 px-6 md:px-0 flex flex-col md:flex-row items-center gap-10">
          {/* Text */}
          <div className="flex-1 space-y-6">
            <h1 className="text-6xl font-bold leading-tight">
              We Build <span className="text-orange-600">Security</span> That Empowers You
            </h1>
            <p className="text-lg text-gray-600 max-w-xl">
              At Ledot Prime, our mission is simple: to help you feel confident and in control of your vehicle. Whether you're managing a fleet or protecting your personal car, we combine cutting-edge GPS technology with sleek design and seamless user experience.
            </p>
          </div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex-1"
          >
            <img
              src="https://images.unsplash.com/photo-1503376780353-7e6692767b70"
              alt="About Ledot Prime"
              className="rounded-2xl shadow-xl object-cover w-full h-[420px]"
            />
          </motion.div>
        </section>

        {/* OUR STORY */}
        <section className="w-full bg-gray-50 py-24 flex justify-center">
          <div className="max-w-5xl px-6 md:px-0 space-y-6 text-center">
            <h2 className="text-4xl font-bold">Our Story</h2>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Ledot Prime started with one simple belief: security shouldn’t be complicated. Vehicles are part of your life, your business, your movement, your identity. Losing one or not knowing where it is can be stressful. That's why we built a platform that gives you eyes on your vehicle anytime, anywhere.
              <br /><br />
              We blend technology with design thinking because the experience should feel premium, intuitive, and effortless.
            </p>
          </div>
        </section>

        {/* VALUES */}
        <section className="w-full max-w-6xl py-24 px-6 grid md:grid-cols-3 gap-10">
          <div className="space-y-4 p-6 rounded-2xl bg-white shadow">
            <h3 className="text-2xl font-bold">Innovation First</h3>
            <p className="text-gray-600">
              Our systems are built with precision and modern engineering to ensure reliability.
            </p>
          </div>
          <div className="space-y-4 p-6 rounded-2xl bg-white shadow">
            <h3 className="text-2xl font-bold">User-Centered</h3>
            <p className="text-gray-600">
              Every feature is designed with one question in mind: does this make your life easier?
            </p>
          </div>
          <div className="space-y-4 p-6 rounded-2xl bg-white shadow">
            <h3 className="text-2xl font-bold">Reliable Support</h3>
            <p className="text-gray-600">
              From installation to daily monitoring, we are with you every step of the way.
            </p>
          </div>
        </section>

        {/* MISSION */}
        <section className="w-full bg-black text-white py-24 px-6 flex justify-center">
          <div className="max-w-5xl text-center space-y-6">
            <h2 className="text-4xl font-bold">Our Mission</h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              To build the most trusted and human-centered vehicle security ecosystem in Africa, merging technology, design, and real-world problem-solving.
            </p>
          </div>
        </section>

        {/* FOUNDER SECTION */}
        <section className="w-full max-w-6xl px-6 py-24 mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">

            {/* Image */}
            <div className="flex justify-center md:justify-start">
              <img
                src={Founder}
                alt="Isaac Kwadwo Duah"
                className="w-72 h-96 md:w-80 md:h-[28rem] object-cover rounded-2xl shadow-xl transition-transform duration-300 hover:scale-105"
              />
            </div>

            {/* Text */}
            <div className="space-y-6 text-center md:text-left">
              <h2 className="text-4xl font-bold text-gray-900">
                Isaac Kwadwo Duah
              </h2>
              <h3 className="text-xl text-gray-500 font-medium">Founder</h3>

              <p className="text-gray-700 text-lg leading-relaxed">
                The visionary behind <span className="font-semibold">Ledot Prime</span>, a leading provider of GPS tracking solutions across Africa. Passionate about vehicle security and fleet monitoring, he created Ledot Prime to help vehicle owners and businesses ensure safety, efficiency, and peace of mind. Today, the company is trusted for GPS car tracking, motorbike tracking, and fleet management solutions, delivering real-time insights for smarter vehicle protection throughout the continent.
              </p>
            </div>

          </div>
        </section>

        {/* CTA */}
        <section className="w-full bg-ledotprimeBlue text-white py-20 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Secure Your Vehicle?</h2>
          <p className="text-lg mb-8 text-blue-100">
            We're here to help you protect what matters.
          </p>
          <button className="bg-white text-black font-semibold px-8 py-3 rounded-xl hover:bg-gray-100">
            Contact Us
          </button>
        </section>

      </div>
    </>
  );
}
